/**
 * Pane-header "?" help affordance (CZNA-366 follow-up).
 *
 * Third member of the help kit alongside `HelpTooltip` (per-field) and
 * `FormHelp` (form-level intro banner, CZNA-380): this one answers
 * "what does this whole screen/pane do", surfaced as a `question-mark`
 * icon button in the pane header that opens a `Popover` on click, rather
 * than an always-visible in-body element. Use it when the help content
 * would otherwise compete for space with the pane's primary content (a
 * collapsed accordion in the body is easy to miss; a `MessageBanner` at
 * the top pushes content down and — in this Stripes theme — renders with
 * a jarring dark background).
 *
 * Deliberately generic: any pane (list or detail) can render
 * `<PaneHelpButton>{...}</PaneHelpButton>` inside its `lastMenu`. The
 * `children` are rendered verbatim inside the popover body, so callers
 * control their own content (plain JSX, or an existing help-content
 * component).
 */
import React from 'react';
import { useIntl } from 'react-intl';
import { Popover, PaneHeaderIconButton } from '@folio/stripes/components';
var DEFAULT_ARIA_LABEL_ID = 'ui-czna-authorities.help.paneButton.ariaLabel';
var PaneHelpButton = function (_a) {
    var children = _a.children, _b = _a.ariaLabelId, ariaLabelId = _b === void 0 ? DEFAULT_ARIA_LABEL_ID : _b;
    var intl = useIntl();
    var ariaLabel = intl.formatMessage({ id: ariaLabelId });
    var renderTrigger = function (_a) {
        var toggle = _a.toggle, open = _a.open, ref = _a.ref;
        return (React.createElement(PaneHeaderIconButton, { ref: ref, icon: "question-mark", "aria-label": ariaLabel, 
            // IconButton renders `aria-expanded={false}` as no attribute; the string keeps "closed".
            // Boolean(): Popover starts from its `open` prop, which is undefined here (CZNA-1459).
            ariaExpanded: String(Boolean(open)), onClick: toggle, "data-testid": "pane-help-button" }));
    };
    // Without an explicit portal, the popover renders inline inside the pane
    // header and gets clipped by `.pane { overflow: hidden; position: absolute }`
    // (same class of bug as Selection/Datepicker dropdowns — see ui-czna-development
    // skill's "Never overflow: hidden on containers with Stripes dropdowns" rule).
    // `document.getElementById('OverlayContainer')` targets the single overlay
    // container `@folio/stripes-core`'s `RootWithIntl` always renders, mirroring
    // the runtime usage in stripes-components' own `InfoPopover.js`.
    //
    // The `@folio/stripes-types` ambient declarations are wrong in two ways here:
    // `Popover`'s `popperProps` is typed as the full (non-partial) `PopperProps`
    // even though `Popover.js` fills in `anchorRef`/`isOpen`/`children` itself —
    // only overrides need be supplied; and `portal` is typed as `ReactPortal`,
    // but `Popper.js` does `PropTypes.instanceOf(Element)` and passes it straight
    // to `ReactDOM.createPortal(overlay, portal)` — it wants the target DOM node.
    // The cast below bridges that upstream type/runtime mismatch (see
    // stripes-components/lib/{Popover,Popper}/*.js); it is not a workaround for
    // anything in this codebase.
    var popperProps = {
        portal: document.getElementById('OverlayContainer'),
    };
    return (React.createElement(Popover, { renderTrigger: renderTrigger, popperProps: popperProps },
        React.createElement("div", { "data-testid": "pane-help-content", style: { maxWidth: '420px', maxHeight: '60vh', overflowY: 'auto' } }, children)));
};
export default PaneHelpButton;
